import { useEffect, useMemo, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "@genni/convex-types";
import { createLogger } from "@/utils/logger";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const logger = createLogger("ProviderKeyManager");

const PROVIDERS = [
  {
    id: "openai" as const,
    name: "OpenAI",
    description: "Required for all LLM-powered workflows including email generation.",
    required: true,
  },
  {
    id: "google_places" as const,
    name: "Google Places",
    description: "Required for lead discovery and business search. Get your API key from Google Cloud Console.",
    required: true,
  },
  {
    id: "findymail" as const,
    name: "FindyMail",
    description: "Required for email enrichment and contact discovery. Get your API key from FindyMail dashboard.",
    required: true,
  },
  {
    id: "tavily" as const,
    name: "Tavily",
    description: "Required for AI research and web enrichment tasks.",
    required: true,
  },
  {
    id: "perplexity" as const,
    name: "Perplexity",
    description: "Required for deep research and business intelligence.",
    required: true,
  },
  // Instantly is managed via InstantlySettings component (available to all users)
  // Commented out - can be reactivated later if needed
  // {
  //   id: "google_maps" as const,
  //   name: "Google Maps (Legacy)",
  //   description: "Legacy Google Maps provider. Use Google Places instead.",
  //   required: false,
  // },
  // {
  //   id: "icypeas" as const,
  //   name: "IcyPeas",
  //   description: "Alternative email enrichment provider.",
  //   required: false,
  // },
  // {
  //   id: "apify" as const,
  //   name: "Apify",
  //   description: "Web scraping and data extraction.",
  //   required: false,
  // },
];

const providerOrder = PROVIDERS.map((provider) => provider.id);

type ProviderId = (typeof providerOrder)[number];

interface ProviderKeyState {
  validated: boolean;
  validatedAt?: number;
  lastError?: string;
  usageCount: number;
  hasKey: boolean;
}

interface ProviderKeyManagerProps {
  plan?: string | null;
}

export function ProviderKeyManager({ plan }: ProviderKeyManagerProps) {
  // Always call ALL hooks at the top level - never conditionally
  const userApiKeys = useQuery(api.userApiKeys.queries.getUserApiKeys);
  const status = useQuery(api.userApiKeys.queries.getApiKeyStatus);
  const validateKey = useAction(api.apiKeys.validateKey);

  const [keyInputs, setKeyInputs] = useState<Record<ProviderId, string>>(() => ({
    openai: "",
    google_places: "",
    findymail: "",
    tavily: "",
    perplexity: "",
    // google_maps: "", // Commented out - legacy
    // icypeas: "", // Commented out
    // apify: "", // Commented out
  }));
  const [validating, setValidating] = useState<ProviderId | null>(null);

  useEffect(() => {
    if (!userApiKeys) {
      return;
    }

    setKeyInputs((prev) => {
      const next = { ...prev };
      for (const provider of providerOrder) {
        const entry = userApiKeys.find((key) => key.provider === provider);
        if (!entry) {
          next[provider] = "";
        }
      }
      return next;
    });
  }, [userApiKeys]);

  const providerState = useMemo(() => {
    const states: Record<ProviderId, ProviderKeyState> = {
      openai: { validated: false, usageCount: 0, hasKey: false },
      google_places: { validated: false, usageCount: 0, hasKey: false },
      findymail: { validated: false, usageCount: 0, hasKey: false },
      tavily: { validated: false, usageCount: 0, hasKey: false },
      perplexity: { validated: false, usageCount: 0, hasKey: false },
      // google_maps: { validated: false, usageCount: 0, hasKey: false }, // Commented out - legacy
      // icypeas: { validated: false, usageCount: 0, hasKey: false }, // Commented out
      // apify: { validated: false, usageCount: 0, hasKey: false }, // Commented out
    };

    for (const key of userApiKeys ?? []) {
      const provider = key.provider as ProviderId;
      if (!providerOrder.includes(provider)) {
        continue;
      }

      states[provider] = {
        validated: Boolean(key.validated),
        validatedAt: key.validatedAt ?? undefined,
        lastError: key.lastError ?? undefined,
        usageCount: key.usageCount ?? 0,
        hasKey: true,
      };
    }

    return states;
  }, [userApiKeys]);

  // Check plan AFTER all hooks are defined
  if (plan !== "enterprise") {
    return null;
  }

  const handleInputChange = (provider: ProviderId, value: string) => {
    setKeyInputs((prev) => ({
      ...prev,
      [provider]: value,
    }));
  };

  const handleValidate = async (provider: ProviderId) => {
    const value = keyInputs[provider]?.trim();
    if (!value) {
      toast.error("Enter an API key before validating.");
      return;
    }

    setValidating(provider);
    try {
      const result = await validateKey({
        provider,
        apiKey: value,
        keyName: `${provider.toUpperCase()} key`,
      });

      if (result.valid) {
        toast.success(`${PROVIDERS.find((p) => p.id === provider)?.name || provider} key validated.`);
        setKeyInputs((prev) => ({
          ...prev,
          [provider]: "",
        }));
      } else {
        toast.error(result.error || "API key validation failed.");
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logger.error("API key validation failed", { provider }, errorObj);
      const message = errorObj.message || "Unable to validate API key. Please try again.";
      toast.error(message);
    } finally {
      setValidating(null);
    }
  };

  // Component already returns early if not enterprise plan (line 104-106)
  // No need for additional check here
  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Enterprise API Keys (Required)</CardTitle>
        <CardDescription>
          As an Enterprise customer, you must provide your own API keys for all providers.
          All five API keys (OpenAI, Google Places, FindyMail, Tavily, Perplexity) are required before you can run any lead generation workflows.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {PROVIDERS.map((provider) => {
          const state = providerState[provider.id];
          const isValid = state?.validated;
          const lastValidated = state?.validatedAt
            ? new Date(state.validatedAt).toLocaleString()
            : null;

          return (
            <div key={provider.id} className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-semibold">{provider.name}</h4>
                    {provider.required && <Badge variant="secondary">Required</Badge>}
                    {isValid && (
                      <Badge className="bg-emerald-500 text-emerald-950 hover:bg-emerald-500/90">
                        Validated
                      </Badge>
                    )}
                    {!isValid && state?.hasKey && (
                      <Badge variant="destructive">Needs attention</Badge>
                    )}
                    {!state?.hasKey && <Badge variant="outline">Not configured</Badge>}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{provider.description}</p>
                  {lastValidated && (
                    <p className="mt-1 text-xs text-muted-foreground">Last validated {lastValidated}</p>
                  )}
                  {state?.lastError && (
                    <p className="mt-1 text-xs text-destructive">{state.lastError}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <Input
                  type="password"
                  placeholder={state?.hasKey ? "Key stored securely. Enter a new key to update." : "Enter API key"}
                  value={keyInputs[provider.id]}
                  onChange={(event) => handleInputChange(provider.id, event.target.value)}
                  className="md:max-w-xl"
                />
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleValidate(provider.id)}
                    disabled={validating === provider.id}
                  >
                    {validating === provider.id ? "Validating..." : "Validate & Save"}
                  </Button>
                  {state?.usageCount ? (
                    <span className="text-xs text-muted-foreground">
                      Used {state.usageCount} {state.usageCount === 1 ? "time" : "times"}
                    </span>
                  ) : null}
                </div>
              </div>
              <Separator />
            </div>
          );
        })}
        {status && (
          <p className="text-xs text-muted-foreground">
            Connected providers: {status.configuredProviders?.length ?? 0} / {providerOrder.length}. Missing required providers:
            {" "}
            {(() => {
              const missing = (status.missingRequiredProviders as string[] | undefined)?.filter((provider): provider is ProviderId =>
                providerOrder.includes(provider as ProviderId),
              );
              return missing && missing.length
                ? missing.map((provider) => provider.replace("_", " ")).join(", ")
                : "None - All required providers configured!";
            })()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
